/**
 * exercise-edit-patrol.js
 */

import React from 'react';
import { Box, Typography, IconButton, FormControl, FormHelperText } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useFormContext, useWatch, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Types } from 'utils/constants';
import usePrevious from 'utils/use-previous';
import { uuidv4 } from 'utils/misc';
import { getPatrolIconName } from '../../training-utils';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import Icon from 'components/icon';
import Button from 'components/button';
import { EditorIconSection } from 'components/icon-section/icon-section';
import HiddenInput from 'components/form-inputs/hidden-input';
import Select from 'components/form-inputs/select';
import DropMenu from 'components/drop-menu';
import PatrolBuildingSearchEdit from '../../answer/edit/patrol-building-search-edit';
import PatrolHumanSearchEdit from '../../answer/edit/patrol-human-search-edit';


const useStyles = makeStyles(theme => ({
  typeHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '4px 0',
    '& .left': {
      padding: '5px 0',
      '& .MuiTypography-root': {
        color: theme.palette.grey.colors[6],
        whiteSpace: 'nowrap',
      },
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        '& .MuiTypography-root': {
          marginTop: theme.spacing(1),
        }
      }
    }
  },
  buttonError: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main
  }
}));


const PatrolTypeEntry = (props) => {
  const { 
    patrolRecord,
    index,
    control,
    setValue,
    drawerState,
    disabled,
    translation,
    gt,
    t,
    removePatrolType,
    formPrefix = '',
    compact,
    confirmChange,
  } = props;
  const classes = useStyles();
  const patrolTypes = useWatch({
    control,
    name: `${formPrefix}patrol.patrolTypes`,
  });
  const patrolType = useWatch({ 
    control, 
    name: `${formPrefix}patrol.patrolTypes[${index}].type`, 
    defaultValue: patrolRecord.type 
  });
  const prevPatrolType = usePrevious(patrolType);


  // update patrol type ids if dropdown patrol type changes
  React.useEffect(() => {
    if (patrolType !== prevPatrolType && prevPatrolType !== undefined) {
      setValue('patrolTypeIds', [...new Set(patrolTypes.map(pt => pt.type))]);
    }
  }, [patrolType, prevPatrolType, patrolTypes, setValue]);


  const content = <>
    <HiddenInput
      name={`${formPrefix}patrol.patrolTypes[${index}].id`}
      defaultValue={patrolRecord.id}
    />
    <HiddenInput
      name={`${formPrefix}patrol.patrolTypes[${index}].type`}
      defaultValue={patrolRecord.type}
    />
    <HelpTextItem
      sidebarText={drawerState.sidebarText}
      title={t(`${formPrefix}patrol.patrolTypes.helpTitle`)}
      text={t(`${formPrefix}patrol.patrolTypes.helpInfo`)}
      noVerticalPadding
    >
      <div className={classes.typeHeader}>
        <div className='flex-hc left'>
          <Select
            name={`${formPrefix}patrol.patrolTypes[${index}].type`}
            label={t('patrol.patrolTypes.label')}
            options={Types.Patrol.Training.entries(gt).map(pt => ({
              ...pt,
              icon: getPatrolIconName(pt.id)
            }))}
            defaultValue={patrolRecord.type}
            style={{ minWidth: 205, marginRight: 16 }}
            hideValueIcon
            disabled={disabled}
            confirmOpen={confirmChange}
          />
          <Typography>
            {patrolTypes.length === 1 ? '' : ` (Scenario)`}
          </Typography>
        </div>

        {patrolTypes.length > 1 &&
          <IconButton
            onClick={async () => {
              if (!confirmChange || await confirmChange()) {
                removePatrolType(index, patrolRecord);
              }
            }}
            disabled={disabled}
            size="large">
            <Icon type='delete' />
          </IconButton>
        }
      </div>
    </HelpTextItem>

    {/* building search form */}
    {patrolType === Types.Patrol.BUILDING_SEARCH &&
      <PatrolBuildingSearchEdit
        patrolRecord={patrolRecord}
        patrolIndex={index}
        isExerciseEditor
        hidden={compact}
        {...props}
      />}

    {/* human search form */}
    {patrolType === Types.Patrol.HUMAN_SEARCH &&
      <PatrolHumanSearchEdit
        patrolRecord={patrolRecord}
        patrolIndex={index}
        isExerciseEditor
        hidden={compact}
        {...props}
      />}
  </>;

  return compact
    ? content
    : <EditorIconSection
        key={`${patrolRecord.hookFormId}`
        }
        iconName={getPatrolIconName(patrolType)}
        iconMarginTop={6}
        helpProps={{
          translation,
          section: 'patrol'
        }}
      >
        {content}
      </EditorIconSection >;
}


const AddPatrolTypeButton = (props) => {
  const { 
    disabled, 
    addPatrolType, 
    confirmChange, 
    gt, 
    compact 
  } = props;
  const classes = useStyles();
  const { formState } = useFormContext();


  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: compact ? 'flex-start': 'center',
        marginTop: compact ? 0 : 5,
        marginBottom: compact ? 0 : {xs: 8, sm: 4}
      }}
    >
      {/* patrol type validation */}
      {Boolean(formState.errors?.patrolTypeIds) &&
        <FormControl error={true} style={{ display: 'flex', marginBottom: 8 }}>
          <FormHelperText sx={{ textAlign: compact ? 'left' : 'center', marginLeft: 0, marginRight: 0 }}>
            {formState.errors.patrolTypeIds.message}
          </FormHelperText>
        </FormControl>}

      <DropMenu
        id='exercise-add-patrol-type'
        label={(isOpen, onClick) =>
          <Button
            level={2}
            className={`${Boolean(formState.errors?.patrolTypeIds) ? classes.buttonError : ''}`}
            aria-label="add patrol type"
            onClick={async (e) => {
              if (!confirmChange || await confirmChange()) {
                onClick(e);
              }
            }}
            disabled={disabled}
            startIcon={<Icon type='add' />}
            endIcon={<Icon
              fontSize='small'
              type='expander'
            />}
          >
            Patrol Type
          </Button>
          }
        aria-label="exercise add patrol type"
        onSelect={item => addPatrolType(item)}
        items={Types.Patrol.Training.entries(gt).map(pt => ({
          key: pt.id,
          name: pt.name,
          icon: getPatrolIconName(pt.id)
        }))}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      />
    </Box>    
  );
}


export default function ExerciseEditPatrol(props) {
  const { 
    storedRecord, 
    workModeValue, 
    compact, 
    confirmChange, 
    formPrefix = '' 
  } = props;
  const { t } = useTranslation(props.translation || 'exerciseEdit');
  const { t: gt } = useTranslation('general');
  const { control, setValue } = useFormContext();
  const { fields: patrolTypes, append, remove } = useFieldArray({
    control,
    name: `${formPrefix}patrol.patrolTypes`,
    keyName: 'hookFormId'
  });


  React.useEffect(() => {
    if (workModeValue === Types.WorkMode.PATROL) {
      setValue('patrolTypeIds', [...new Set(patrolTypes.map(pt => pt.type))]);
    }

  // only when work mode changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workModeValue, setValue]);


  const addPatrolType = (type) => {
    append({
      id: null,
      type: type.key,
      order: patrolTypes.length + 1,
      uuid: uuidv4(),  // local unique key before persistence
    });

    setTimeout(() => {
      setValue(
        'patrolTypeIds', 
        [...new Set([...patrolTypes.map(pt => pt.type), type.key])],
        { shouldValidate: true });
    });
  }


  const removePatrolType = (index, type) => {
    remove(index);

    setTimeout(() => {
      setValue(
        'patrolTypeIds', 
        [...new Set(patrolTypes
          .filter(pt => pt.hookFormId !== type.hookFormId)
          .map(pt => pt.type))]);
    });
  }


  return (
    <>
      {workModeValue === Types.WorkMode.PATROL &&
      <>
        <HiddenInput
          name='patrolTypeIds'
          defaultValue={[]}
          rules={{
            validate: (val) => val.length > 0 || t(`patrol.patrolTypes.errMsgEmpty`)
          }}
        />

        {patrolTypes.map((patrolRecord, i) => (
          <div key={patrolRecord.hookFormId}>
            <PatrolTypeEntry
              {...props}
              {...{
                patrolRecord,
                control,
                setValue,
                gt,
                t,
                removePatrolType,
              }}
              index={i}
              disabled={storedRecord.fullStats?.complete > 0}
            />
          </div>
        ))}

        <AddPatrolTypeButton
          {...{ addPatrolType, gt, confirmChange, compact }}
          disabled={storedRecord.fullStats?.complete > 0}
        />

      </>}
    </>
  );
}